/**
 * Configuration file for L1J Web Client
 * Contains server settings, game constants, and client options
 *
 * 현재 런타임에서 실제로 참조되는 그룹은 `ServerConfig` 뿐이다
 * (ConnectionManager / LoginScene / MapCollision). 나머지 그룹은 서버·게이트웨이와의
 * 값 대조를 위한 참조용 상수이며, 코드에서 읽어 쓰기 시작할 때 실제 동작과
 * 일치하는지 반드시 재확인할 것.
 *
 * 좌표 관련 상수는 여기에 두지 않는다. 타일 규격과 아이소메트릭 변환은
 * `src/utils/isometric.ts`(TILE_WIDTH=48, TILE_HEIGHT=24)가 단일 source of truth다.
 */

import { getRuntimeConfig } from './runtimeConfig';

/**
 * Server configuration
 */
export const ServerConfig = {
  /**
   * Gateway WebSocket server URL
   *
   * getter인 이유: 배포 후 `config.json`으로 주소를 덮어쓸 수 있어야 하는데,
   * 그 파일은 부팅 시점에 비동기로 로드된다(`runtimeConfig.ts`). 모듈 로드 시점에
   * 값을 고정하면 로드 완료 전에 굳어버리므로 매 참조마다 다시 계산한다.
   */
  get GATEWAY_URL(): string {
    return getRuntimeConfig().gatewayUrl || import.meta.env.VITE_GATEWAY_URL || getGatewayUrl();
  },

  /** 맵 통행 가능 여부(벽 충돌) HTTP 서버 URL — 게이트웨이 WebSocket 포트 + 1 */
  get MAP_HTTP_URL(): string {
    return getRuntimeConfig().mapHttpUrl || import.meta.env.VITE_MAP_HTTP_URL || getMapHttpUrl();
  },

  /** Game server host (for direct connection fallback) */
  GAME_SERVER_HOST: import.meta.env.VITE_GAME_SERVER_HOST || '127.0.0.1',

  /** Game server port */
  GAME_SERVER_PORT: parseInt(import.meta.env.VITE_GAME_SERVER_PORT || '2000', 10),

  /** Connection timeout in milliseconds */
  CONNECTION_TIMEOUT: 10000,

  /** Reconnection delay in milliseconds */
  RECONNECT_DELAY: 3000,

  /** Maximum reconnection attempts */
  MAX_RECONNECT_ATTEMPTS: 5,

  /** Ping interval in milliseconds */
  PING_INTERVAL: 5000,

  /** Pong timeout in milliseconds */
  PONG_TIMEOUT: 15000
} as const;

/**
 * Client version configuration (L1J-JP 3.63C)
 */
export const ClientConfig = {
  /** Protocol version (3.63C encoded as little-endian short = 0x003C) */
  PROTOCOL_VERSION: 0x3c, // 60 decimal - matches "3.63C"

  /** Client build number (matches server S_ServerVersion.SERVER_VERSION = 120913203 = 0x073ACA93) */
  BUILD_NUMBER: 120913203, // 0x073ACA93

  /** Client type identifier */
  CLIENT_TYPE: 0x01,

  /** Expected server version string */
  EXPECTED_SERVER_VERSION: '3.63C'

  // Note: For 3.63C Japanese client, version info is:
  // - CLIENT_VERSION opcode: 127 (0x7F)
  // - Version bytes: 0x3C 0x00 (little-endian short = 60)
  // - Unknown bytes: 0xFF 0xFF 0xFF 0xFF
  // - Unknown byte: 0x32
  // - Build number: 0x07 0x3A 0xCA 0x93 (big-endian; Netty writeInt = 120913203)
} as const;

/**
 * Game constants
 */
export const GameConfig = {
  /** Maximum character name length */
  MAX_CHAR_NAME_LENGTH: 12,

  /** Maximum chat message length */
  MAX_CHAT_LENGTH: 128,

  /** Maximum inventory slots */
  MAX_INVENTORY_SLOTS: 128,

  /** Player movement speed (미사용 참조값). 실제 이동은 타일 단위 스텝이며 픽셀 속도가
   *  아니라 타일당 소요 시간으로 관리한다 — `GameScene.MS_PER_TILE`(현재 340ms/타일)이
   *  진짜 속도다. 그 값은 서버 `config/server.properties.xml`의 `CheckStrictness`와
   *  DB `spr_actions`의 보행 속도에서 역산한 것이라 여기서 바꿀 수 없다. */
  PLAYER_MOVE_SPEED: 6,

  /** Player run multiplier */
  PLAYER_RUN_MULTIPLIER: 1.5,

  /** Attack range for melee (타일 단위) */
  ATTACK_RANGE: 1,

  /** Maximum chat history messages */
  MAX_CHAT_HISTORY: 100,

  /** Camera follow distance (픽셀) */
  CAMERA_FOLLOW_DISTANCE: 200,

  // TILE_SIZE는 제거됨. 값(64)이 실제 타일 규격과 달랐고 참조하는 코드도 없었다.
  // 타일 크기가 필요하면 `src/utils/isometric.ts`의 TILE_WIDTH(48) / TILE_HEIGHT(24)를
  // import해서 쓸 것. 아이소메트릭 타일은 정사각형이 아니므로 단일 "TILE_SIZE"로
  // 표현할 수 없다.

  /** Client-side prediction smoothing factor */
  PREDICTION_SMOOTHING: 0.8,

  /** Server position update rate (expected packets per second) */
  SERVER_UPDATE_RATE: 10,

  /** Client send rate for movement (packets per second) */
  CLIENT_MOVE_RATE: 10,

  /** Movement throttle (minimum ms between move packets) */
  MOVE_THROTTLE_MS: 100
} as const;

/**
 * UI configuration
 */
export const UIConfig = {
  /** Enable debug UI */
  DEBUG_MODE: import.meta.env.DEV,

  /** Show FPS counter */
  SHOW_FPS: true,

  /** Show position coordinates */
  SHOW_COORDINATES: true,

  /** UI scale factor */
  UI_SCALE: 1.0,

  /** Chat box width */
  CHAT_BOX_WIDTH: 400,

  /** Chat box height */
  CHAT_BOX_HEIGHT: 200,

  /** Inventory grid columns */
  INVENTORY_COLS: 8,

  /** Inventory grid rows */
  INVENTORY_ROWS: 4,

  /** Status bar height */
  STATUS_BAR_HEIGHT: 40,

  /** Mini map size */
  MINI_MAP_SIZE: 150,

  /** Enable particle effects */
  ENABLE_PARTICLES: true,

  /** Enable screen shake */
  ENABLE_SCREEN_SHAKE: true,

  /** Target FPS */
  TARGET_FPS: 60,

  /** Enable vsync */
  VSYNC: true
} as const;

/**
 * Network protocol configuration (L1J-JP 3.63C)
 *
 * 주의: 웹 클라이언트는 바이너리 패킷을 직접 만들거나 cipher를 돌리지 않는다.
 * 핸드셰이크/암복호화/opcode 조립은 전부 게이트웨이(`l1j-gateway/`)가 담당하며,
 * 아래 값들은 게이트웨이·서버 구현과 대조하기 위한 참조용 문서 상수다.
 */
export const ProtocolConfig = {
  /** The seed/key is supplied by the server per-connection (random).
   *  No fixed initial seed is required; the gateway echoes whatever it receives. */
  INITIAL_SEED: 0,

  /** Seed for encryption key derivation (matches jp.l1j.server.utils.Cipher._2). */
  ENCRYPTION_SEED: 0x930fd7e2,

  /** Server's S_OPCODE_INITPACKET marker for the first frame (0xA1 = 161). */
  S_OPCODE_INITPACKET: 0xa1,

  /** FIRST_PACKET bytes used in the 3.63C handshake echo. */
  FIRST_PACKET_BYTES: [
    0x4d, 0xa7, 0x32, 0x5b, 0x2e, 0x7e, 0xed, 0x25, 0xa5, 0x54, 0xb3
  ],

  /** Minimum packet size (server requires 4 bytes minimum) */
  MIN_PACKET_SIZE: 4,

  /** Maximum packet size */
  MAX_PACKET_SIZE: 65535,

  /** Enable packet logging */
  LOG_PACKETS: import.meta.env.DEV,

  /** 웹 클라이언트 자체 암호화 여부. 항상 false — cipher는 게이트웨이가 전담한다. */
  ENCRYPTION_ENABLED: false,

  // 3.63C opcode 참조 사본. (예전 PacketBuilder/PacketParser가 쓰던 값이지만 두 파일은
  // 삭제됐다. 현재 이 상수들을 읽는 코드는 없으며, 실제 opcode 매핑의 source of truth는
  // `src/network/Protocol.ts`와 게이트웨이의 `l1j/gateway/crypto/Opcodes.java`다.)
  /** Opcode for login packet (LOGINPACKET) */
  OPCODE_LOGINPACKET: 0x39, // 57

  /** Opcode for client version (CLIENTVERSION) */
  OPCODE_CLIENTVERSION: 0x7f, // 127

  /** Opcode for move character (MOVECHAR) */
  OPCODE_MOVECHAR: 0x5f, // 95

  /** Opcode for attack (ATTACK) */
  OPCODE_ATTACK: 0x44, // 68

  /** Opcode for chat (CHAT) */
  OPCODE_CHAT: 0xbe, // 190

  /** Opcode for common click (COMMONCLICK) */
  OPCODE_COMMONCLICK: 0x35, // 53

  /** Opcode for login to server (LOGINTOSERVER) */
  OPCODE_LOGINTOSERVER: 0x83, // 131

  /** Opcode for login to server OK (LOGINTOSERVEROK) */
  OPCODE_LOGINTOSERVEROK: 0x4b, // 75

  /** Opcode for server version (SERVERVERSION) */
  OPCODE_SERVERVERSION: 0x97, // 151

  /** Opcode for login result (LOGINRESULT) */
  OPCODE_LOGINRESULT: 0x33, // 51

  /** Opcode for char list (CHARLIST) */
  OPCODE_CHARLIST: 0xb8, // 184

  /** Opcode for move object (MOVEOBJECT) */
  OPCODE_MOVEOBJECT: 0x7a, // 122

  /** Opcode for S_INITPACKET (handshake marker) */
  OPCODE_INITPACKET: 0xa1, // 161
} as const;

/**
 * Input configuration
 */
export const InputConfig = {
  /** Keyboard repeat delay */
  KEY_REPEAT_DELAY: 200,

  /** Keyboard repeat interval */
  KEY_REPEAT_INTERVAL: 50,

  /** Mouse sensitivity */
  MOUSE_SENSITIVITY: 1.0,

  /** Enable mouse look */
  MOUSE_LOOK: false,

  /** Invert Y axis */
  INVERT_Y: false,

  /** Chat hotkey */
  CHAT_HOTKEY: 'Enter',

  /** Inventory hotkey */
  INVENTORY_HOTKEY: 'i',

  /** Map hotkey */
  MAP_HOTKEY: 'm',

  /** Character hotkey */
  CHARACTER_HOTKEY: 'c',

  /** Attack hotkey */
  ATTACK_HOTKEY: 'Space',

  /** Pickup hotkey */
  PICKUP_HOTKEY: 'z',

  /** Run hotkey */
  RUN_HOTKEY: 'Shift',

  /** Sit hotkey */
  SIT_HOTKEY: 'x',

  /** Menu hotkey */
  MENU_HOTKEY: 'Escape'
} as const;

/**
 * Asset configuration
 */
export const AssetConfig = {
  /** Graphics directory */
  GFX_DIR: '/assets/gfx',

  /** Character sprites subdirectory */
  CHARS_DIR: '/assets/gfx/chars',

  /** NPC sprites subdirectory */
  NPCS_DIR: '/assets/gfx/npcs',

  /** Monster sprites subdirectory */
  MONSTERS_DIR: '/assets/gfx/monsters',

  /** Item sprites subdirectory */
  ITEMS_DIR: '/assets/gfx/items',

  /** Tilesets directory */
  TILESETS_DIR: '/assets/tilesets',

  /** Audio directory */
  AUDIO_DIR: '/assets/audio',

  /** Loading screen image */
  LOADING_IMAGE: '/assets/ui/loading.png',

  /** Login background image */
  LOGIN_BACKGROUND: '/assets/ui/login_bg.png',

  /** Default character sprite */
  DEFAULT_CHAR_SPRITE: '/assets/gfx/chars/human_male.png',

  /** Default NPC sprite */
  DEFAULT_NPC_SPRITE: '/assets/gfx/npcs/default.png',

  /** Default monster sprite */
  DEFAULT_MONSTER_SPRITE: '/assets/gfx/monsters/default.png'
} as const;

/**
 * Debug configuration
 */
export const DebugConfig = {
  /** Enable debug mode */
  ENABLED: import.meta.env.DEV,

  /** Log all sent packets */
  LOG_OUTGOING_PACKETS: true,

  /** Log all received packets */
  LOG_INCOMING_PACKETS: true,

  /** Log network events */
  LOG_NETWORK: true,

  /** Log game events */
  LOG_GAME: true,

  /** Log UI events */
  LOG_UI: true,

  /** Show collision boxes */
  SHOW_COLLISION_BOXES: false,

  /** Show entity IDs */
  SHOW_ENTITY_IDS: false,

  /** Show pathfinding */
  SHOW_PATHFINDING: false,

  /** Simulate network latency */
  SIMULATE_LATENCY: false,

  /** Simulated latency in ms */
  SIMULATED_LATENCY_MS: 0,

  /** Show server positions (vs client predicted) */
  SHOW_SERVER_POSITIONS: false,

  /** Entity render distance */
  ENTITY_RENDER_DISTANCE: 1000
} as const;

/**
 * Feature flags
 */
export const FeatureFlags = {
  /** Enable auto-login (for testing) */
  AUTO_LOGIN: false,

  /** Auto-login account */
  AUTO_LOGIN_ACCOUNT: 'test',

  /** Auto-login password */
  AUTO_LOGIN_PASSWORD: 'test',

  /** Enable character auto-select */
  AUTO_SELECT_CHAR: false,

  /** Auto-select character name */
  AUTO_SELECT_CHAR_NAME: 'TestChar',

  /** Enable debug login screen */
  DEBUG_LOGIN: true,

  /** Enable simplified rendering (for low-end devices) */
  SIMPLIFIED_RENDERING: false,

  /** Enable sound */
  SOUND_ENABLED: true,

  /** Enable music */
  MUSIC_ENABLED: true,

  /** Enable chat */
  CHAT_ENABLED: true,

  /** Enable party system */
  PARTY_ENABLED: true,

  /** Enable clan system */
  CLAN_ENABLED: true,

  /** Enable trading */
  TRADE_ENABLED: true
} as const;

// Re-export for convenience
export const Config = {
  server: ServerConfig,
  client: ClientConfig,
  game: GameConfig,
  ui: UIConfig,
  protocol: ProtocolConfig,
  input: InputConfig,
  assets: AssetConfig,
  debug: DebugConfig,
  features: FeatureFlags
};

export type Config = typeof Config;

/**
 * Get Gateway URL based on current location.
 *
 * 이 함수는 최후의 폴백이다. 전체 우선순위는 ServerConfig.GATEWAY_URL getter가 정한다:
 *   0) 배포 산출물의 config.json (runtimeConfig.ts) — 정적 호스팅 배포용 override
 *   1) Explicit VITE_GATEWAY_URL env wins.
 *   2) Same hostname as the page (window.location.hostname).
 *      - http://localhost:5173   → ws://localhost:8080
 *      - http://127.0.0.1:5173   → ws://127.0.0.1:8080
 *      - http://100.73.225.52:5173 → ws://100.73.225.52:8080
 *      This way the browser reaches the same host where the Vite dev server is running,
 *      which is the same machine the Gateway is bound to (port 8080 on 0.0.0.0).
 */
function getGatewayUrl(): string {
  if (import.meta.env.VITE_GATEWAY_URL) {
    return import.meta.env.VITE_GATEWAY_URL;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.hostname}:8080`;
}

/**
 * Get map passability HTTP server URL. 게이트웨이의 WebSocket 포트(8080) + 1(8081)에서
 * 서빙되므로 GATEWAY_URL과 동일한 호스트 규칙을 따르되 http(s)/포트만 바꾼다.
 */
function getMapHttpUrl(): string {
  if (import.meta.env.VITE_MAP_HTTP_URL) {
    return import.meta.env.VITE_MAP_HTTP_URL;
  }
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${window.location.hostname}:8081`;
}
